import {
  Badge,
  Button,
  CloseButton,
  Container,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import type { CompetitionClass, CompetitionLevel, Dog, Run } from "@my-agility-qs/shared";
// import { computeDogLevel } from "@my-agility-qs/shared";
import { IconArrowLeft, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { RunDetailsModal } from "../components/RunDetailsModal";
import { useAuth } from "../contexts/AuthContext";
import { useURLState } from "../hooks/useURLState";
import { dogsApi, runsApi } from "../lib/api";
import { CLASS_DISPLAY_NAMES, isPremierClass } from "../lib/constants";

type SortField = "date" | "dog" | "class" | "level";
type SortDirection = "asc" | "desc";

export const ViewRunsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [, navigate] = useLocation();

  // URL state management - single source of truth
  const [filters, setFilters] = useURLState({
    dog: null as string | null,
    class: null as CompetitionClass | null,
    level: "all" as "current" | "all",
    qq: null as string | null,
    from: null as string | null,
  });

  // Extract individual filter values for easier access
  const { dog: selectedDogId, class: selectedClass, level: selectedLevel, qq, from } = filters;

  // Double Q mode: show only same-day Masters Standard + Jumpers Q pairs.
  // While active, `level` is repurposed: "current" = QQs since the last MACH.
  const qqMode = qq === "1";

  // Local state for non-URL state
  const [showOnlyQs, setShowOnlyQs] = useState(false);
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [selectedRun, setSelectedRun] = useState<Run | null>(null);

  const {
    data: runs = [],
    isLoading: runsLoading,
    error: runsError,
  } = useQuery({
    queryKey: ["runs"],
    queryFn: runsApi.getAllRuns,
  });
  const {
    data: dogs = [],
    isLoading: dogsLoading,
    error: dogsError,
  } = useQuery({
    queryKey: ["dogs"],
    queryFn: dogsApi.getAllDogs,
  });

  const hardDeleteRunMutation = useMutation({
    mutationFn: runsApi.hardDelete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["runs"] });
      queryClient.invalidateQueries({ queryKey: ["progress"] });
      notifications.show({
        title: "Success",
        message: "Run has been permanently deleted",
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        message: `Failed to delete run: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        color: "red",
      });
    },
  });

  const handleHardDeleteRun = (run: Run) => {
    const dogName = dogNameMap[run.dogId] || "Unknown Dog";
    modals.openConfirmModal({
      title: "Permanently Delete Run",
      children: (
        <Stack gap="sm">
          <Text size="sm">
            Are you sure you want to permanently delete this run for <strong>{dogName}</strong>?
          </Text>
          <Text size="sm" c="dimmed">
            {formatDate(run.date)} • {getClassDisplayName(run.class)}{!isPremierClass(run.class) ? ` • ${run.level}` : ""}
          </Text>
          <Text size="sm" c="orange">
            <strong>Warning:</strong> This action cannot be undone.
          </Text>
        </Stack>
      ),
      labels: { confirm: "Delete Permanently", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => hardDeleteRunMutation.mutate(run.id),
    });
  };

  const isLoading = runsLoading || dogsLoading;
  const error = runsError || dogsError;

  // Create a map of dog IDs to dog names for quick lookup
  const dogNameMap = dogs.reduce((acc: Record<string, string>, dog: Dog) => {
    acc[dog.id] = dog.name;
    return acc;
  }, {});

  // Helper function to get display name for class
  const getClassDisplayName = (className: string): string => {
    // Find the display name key that maps to this full name
    const displayKey = Object.entries(CLASS_DISPLAY_NAMES).find(
      ([, fullValue]) => fullValue === className
    )?.[0];
    return displayKey || className; // Fallback to original if not found
  };

  const formatDate = (dateString: string): string => {
    return dayjs(dateString).format("M/D/YYYY");
  };

  const getPlacementBadge = (placement: number | null) => {
    if (!placement) return null;

    const colors = {
      1: "blue",
      2: "red",
      3: "yellow",
      4: "gray",
    } as const;

    return (
      <Badge color={colors[placement as keyof typeof colors] || "gray"} variant="filled" size="sm">
        {placement}
      </Badge>
    );
  };

  // Get distinct classes from actual data
  const distinctClasses = useMemo(() => {
    const classSet = new Set(runs.map((run) => run.class));
    return Array.from(classSet).sort();
  }, [runs]);

  // Helper to determine current level for a dog in a class
  const getCurrentLevel = useCallback(
    (dogId: string, className: string): CompetitionLevel | null => {
      const dogRuns = runs.filter((r) => r.dogId === dogId && r.class === className && r.qualified);
      if (dogRuns.length === 0) return "Novice";

      const levelCounts = dogRuns.reduce((acc, run) => {
        acc[run.level] = (acc[run.level] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Determine current level based on progression rules
      if (levelCounts["Masters"] || (levelCounts["Excellent"] && levelCounts["Excellent"] >= 3)) {
        return "Masters";
      } else if (levelCounts["Excellent"] || (levelCounts["Open"] && levelCounts["Open"] >= 3)) {
        return "Excellent";
      } else if (levelCounts["Open"] || (levelCounts["Novice"] && levelCounts["Novice"] >= 3)) {
        return "Open";
      }
      return "Novice";
    },
    [runs]
  );

  // Double Q reconstruction. Mirrors calculateDoubleQs/calculateMachProgress in
  // server/src/utils/progressCalculations.ts (the source of truth): a QQ is a
  // same-day Masters Standard + Jumpers Q pair, each MACH consumes the 20
  // earliest QQs (baseline QQs first), and the remainder rolls forward.
  const qqInfo = useMemo(() => {
    if (!qqMode) return null;

    const machRunsByDog = new Map<string, Run[]>();
    for (const run of runs) {
      if (
        run.qualified &&
        run.level === "Masters" &&
        (run.class === "Standard" || run.class === "Jumpers")
      ) {
        if (!machRunsByDog.has(run.dogId)) machRunsByDog.set(run.dogId, []);
        machRunsByDog.get(run.dogId)!.push(run);
      }
    }

    const runIds = new Set<string>();
    let pairCount = 0;
    let baselineCount = 0;

    for (const dog of dogs) {
      if (selectedDogId && dog.id !== selectedDogId) continue;

      const dogRuns = machRunsByDog.get(dog.id) ?? [];
      const baselineQQs = dog.baseline?.doubleQs ?? 0;
      const baselinePoints = dog.baseline?.machPoints ?? 0;
      if (dogRuns.length === 0 && baselineQQs === 0) continue;

      const runsByDate = new Map<string, Run[]>();
      for (const run of dogRuns) {
        if (!runsByDate.has(run.date)) runsByDate.set(run.date, []);
        runsByDate.get(run.date)!.push(run);
      }
      const pairDates = Array.from(runsByDate.entries())
        .filter(
          ([, dateRuns]) =>
            dateRuns.some((r) => r.class === "Standard") &&
            dateRuns.some((r) => r.class === "Jumpers")
        )
        .map(([date]) => date)
        .sort(); // ascending, so earliest QQs are consumed first

      // "Since last MACH": skip the QQs consumed by completed MACHs
      let consumed = 0;
      if (selectedLevel === "current") {
        const totalQQs = pairDates.length + baselineQQs;
        const totalPoints =
          dogRuns.reduce((sum, r) => sum + (r.machPoints || 0), 0) + baselinePoints;
        const completeMachs = Math.min(
          Math.floor(totalPoints / 750),
          Math.floor(totalQQs / 20)
        );
        consumed = completeMachs * 20;
      }

      const skipPairs = Math.max(0, consumed - baselineQQs);
      const datesInScope = pairDates.slice(skipPairs);
      pairCount += datesInScope.length;
      baselineCount += Math.max(0, baselineQQs - consumed);

      for (const date of datesInScope) {
        for (const run of runsByDate.get(date)!) runIds.add(run.id);
      }
    }

    return { runIds, count: pairCount + baselineCount, baselineCount };
  }, [qqMode, runs, dogs, selectedDogId, selectedLevel]);

  // Filter and sort runs
  const filteredAndSortedRuns = useMemo(() => {
    let filtered = runs;

    // Apply dog filter
    if (selectedDogId) {
      filtered = filtered.filter((run) => run.dogId === selectedDogId);
    }

    if (qqInfo) {
      // QQ mode implies class (Masters Std/JWW), level, and Q status, so the
      // pair membership set replaces the class/level/Q filters entirely
      filtered = filtered.filter((run) => qqInfo.runIds.has(run.id));
    } else {
      // Apply class filter
      if (selectedClass) {
        filtered = filtered.filter((run) => run.class === selectedClass);
      }

      // Apply level filter
      if (selectedLevel === "current") {
        filtered = filtered.filter((run) => {
          const currentLevel = getCurrentLevel(run.dogId, run.class);
          return run.level === currentLevel;
        });
      }

      // Apply Q filter (only if not tracking Qs only, since in that mode all runs are Qs)
      if (showOnlyQs && !(user?.trackQsOnly ?? true)) {
        filtered = filtered.filter((run) => run.qualified);
      }
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case "date":
          comparison = dayjs(a.date).valueOf() - dayjs(b.date).valueOf();
          break;
        case "dog":
          comparison = (dogNameMap[a.dogId] || "").localeCompare(dogNameMap[b.dogId] || "");
          break;
        case "class":
          comparison = getClassDisplayName(a.class).localeCompare(getClassDisplayName(b.class));
          break;
        case "level": {
          // Custom sort for levels (Masters > Excellent > Open > Novice)
          const levelOrder = { Masters: 4, Excellent: 3, Open: 2, Novice: 1 };
          comparison =
            (levelOrder[a.level as keyof typeof levelOrder] || 0) -
            (levelOrder[b.level as keyof typeof levelOrder] || 0);
          break;
        }
      }

      return sortDirection === "desc" ? -comparison : comparison;
    });

    return sorted;
  }, [
    runs,
    selectedDogId,
    selectedClass,
    selectedLevel,
    qqInfo,
    showOnlyQs,
    sortField,
    sortDirection,
    dogNameMap,
    user?.trackQsOnly,
    getCurrentLevel,
  ]);

  // Handle column header clicks for sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Render sort icon
  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <IconChevronUp size={14} style={{ marginLeft: 4 }} />
    ) : (
      <IconChevronDown size={14} style={{ marginLeft: 4 }} />
    );
  };
  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <Stack align="center" mt="xl">
          <Loader size="lg" />
          <Text>Loading runs...</Text>
        </Stack>
      </Container>
    );
  }
  if (error) {
    return (
      <Container size="xl" py="md">
        <Stack>
          <Group justify="space-between" align="center" w="100%" style={{ position: "relative" }}>
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate("/")}
              size="sm"
            >
              Back
            </Button>
            <Title
              order={3}
              style={{
                margin: 0,
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              View Runs
            </Title>
            <div></div>
          </Group>
          <Text c="red">
            Error loading runs: {error instanceof Error ? error.message : "Unknown error"}
          </Text>
        </Stack>
      </Container>
    );
  }
  return (
    <Container size="xl" py="md">
      <Stack>
        <Group justify="space-between" align="center" w="100%" style={{ position: "relative" }}>
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(from === "title-progress" ? "/title-progress" : "/")}
            size="sm"
          >
            Back
          </Button>
          <Title
            order={3}
            style={{
              margin: 0,
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            View Runs
          </Title>
          <Text c="dimmed" size="sm">
            {qqInfo
              ? `${qqInfo.count} Double Q${qqInfo.count !== 1 ? "s" : ""}`
              : `${filteredAndSortedRuns.length} run${filteredAndSortedRuns.length !== 1 ? "s" : ""}`}
          </Text>
        </Group>{" "}
        {/* Filters */}
        <Paper withBorder p="sm" radius="md">
          <Stack gap="sm">
            {/* Dog filter */}
            <SimpleGrid
              cols={(() => {
                const activeDogs = dogs.filter((dog) => dog.active);
                return activeDogs.length === 2 ? 3 : 2;
              })()}
              spacing="xs"
            >
              <Button
                variant={selectedDogId === null ? "filled" : "outline"}
                color={selectedDogId === null ? "blue" : "gray"}
                size="xs"
                onClick={() => setFilters({ dog: null })}
              >
                All Dogs
              </Button>
              {dogs
                .filter((dog) => dog.active)
                .map((dog) => (
                  <Button
                    key={dog.id}
                    variant={selectedDogId === dog.id ? "filled" : "outline"}
                    color={selectedDogId === dog.id ? "blue" : "gray"}
                    size="xs"
                    onClick={() => setFilters({ dog: dog.id })}
                  >
                    {dog.name}
                  </Button>
                ))}
            </SimpleGrid>

            {/* Class filter — replaced by a QQ indicator in qq mode, since
                class (Masters Std/JWW) and Q status are implied there */}
            {qqMode ? (
              <Group
                justify="space-between"
                wrap="nowrap"
                style={{
                  backgroundColor: "var(--mantine-color-orange-0)",
                  padding: "4px 8px",
                  borderRadius: "8px",
                }}
              >
                <Group gap="xs" wrap="nowrap">
                  <Badge color="orange" variant="filled" size="sm">
                    QQ
                  </Badge>
                  <Text size="sm" fw={500}>
                    Showing Double Qs only
                  </Text>
                </Group>
                <CloseButton
                  size="sm"
                  aria-label="Clear Double Q filter"
                  onClick={() => setFilters({ qq: null, level: "all" })}
                />
              </Group>
            ) : (
              <SimpleGrid
                cols={(() => {
                  return distinctClasses.length === 2 ? 3 : 2;
                })()}
                spacing="xs"
              >
                <Button
                  variant={selectedClass === null ? "filled" : "outline"}
                  color={selectedClass === null ? "blue" : "gray"}
                  size="xs"
                  onClick={() => setFilters({ class: null })}
                >
                  All Classes
                </Button>
                {distinctClasses.map((className) => (
                  <Button
                    key={className}
                    variant={selectedClass === className ? "filled" : "outline"}
                    color={selectedClass === className ? "blue" : "gray"}
                    size="xs"
                    onClick={() => setFilters({ class: className as CompetitionClass })}
                  >
                    {getClassDisplayName(className)}
                  </Button>
                ))}
              </SimpleGrid>
            )}

            {/* Level filter. In qq mode it is repurposed as the QQ scope toggle */}
            <Group grow gap="xs">
              <SegmentedControl
                size="xs"
                value={selectedLevel ?? "all"}
                onChange={(v) => setFilters({ level: v as "current" | "all" })}
                data={
                  qqMode
                    ? [
                        { label: "All QQs", value: "all" },
                        { label: "Since Last MACH", value: "current" },
                      ]
                    : [
                        { label: "All Levels", value: "all" },
                        { label: "Current Level", value: "current" },
                      ]
                }
              />
            </Group>

            {/* Show only Qs toggle */}
            {!qqMode && !(user?.trackQsOnly ?? true) && (
              <Switch
                label="Show only Qs"
                checked={showOnlyQs}
                onChange={(event) => setShowOnlyQs(event.currentTarget.checked)}
                color="green"
                size="sm"
              />
            )}
          </Stack>
        </Paper>
        {filteredAndSortedRuns.length === 0 ? (
          <Paper withBorder p="xl" radius="md">
            <Stack align="center">
              <Text size="lg" c="dimmed">
                {runs.length === 0
                  ? "No runs recorded yet"
                  : qqMode
                    ? "No Double Qs recorded yet"
                    : "No runs match your filters"}
              </Text>
              {runs.length === 0 && (
                <Button onClick={() => navigate("/add-run")}>Add Your First Run</Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <Paper withBorder shadow="sm" radius="md">
            <div style={{ overflowX: "auto" }}>
              <Table highlightOnHover striped stickyHeader>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th
                      style={{ cursor: "pointer", userSelect: "none", minWidth: "80px" }}
                      onClick={() => handleSort("date")}
                    >
                      <Group gap={4} wrap="nowrap">
                        Date
                        {renderSortIcon("date")}
                      </Group>
                    </Table.Th>
                    <Table.Th
                      style={{ cursor: "pointer", userSelect: "none", minWidth: "80px" }}
                      onClick={() => handleSort("dog")}
                    >
                      <Group gap={4} wrap="nowrap">
                        Dog
                        {renderSortIcon("dog")}
                      </Group>
                    </Table.Th>
                    <Table.Th
                      style={{ cursor: "pointer", userSelect: "none", minWidth: "70px" }}
                      onClick={() => handleSort("class")}
                    >
                      <Group gap={4} wrap="nowrap">
                        Class
                        {renderSortIcon("class")}
                      </Group>
                    </Table.Th>
                    <Table.Th
                      style={{ cursor: "pointer", userSelect: "none", minWidth: "80px" }}
                      onClick={() => handleSort("level")}
                    >
                      <Group gap={4} wrap="nowrap">
                        Level
                        {renderSortIcon("level")}
                      </Group>
                    </Table.Th>
                    {/* Result column - only show if tracking NQ runs */}
                    {!(user?.trackQsOnly ?? true) && <Table.Th style={{ minWidth: "60px" }}>Result</Table.Th>}
                    <Table.Th style={{ minWidth: "60px" }}>Place</Table.Th>
                    <Table.Th style={{ minWidth: "60px" }}>Points</Table.Th>
                    <Table.Th style={{ minWidth: "100px" }}>Location</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {filteredAndSortedRuns.map((run: Run) => (
                    <Table.Tr
                      key={run.id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedRun(run)}
                    >
                      <Table.Td>
                        <Text size="sm">{formatDate(run.date)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" fw={500}>
                          {dogNameMap[run.dogId] || "Unknown Dog"}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{getClassDisplayName(run.class)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{isPremierClass(run.class) ? "—" : run.level}</Text>
                      </Table.Td>
                      {/* Result column - only show if tracking NQ runs */}
                      {!(user?.trackQsOnly ?? true) && (
                        <Table.Td>
                          <Badge
                            color={run.qualified ? "green" : "red"}
                            variant={run.qualified ? "filled" : "outline"}
                            size="sm"
                          >
                            {run.qualified ? "Q" : "NQ"}
                          </Badge>
                        </Table.Td>
                      )}
                      <Table.Td>{getPlacementBadge(run.placement)}</Table.Td>
                      <Table.Td>
                        <Text size="sm">{run.machPoints ?? "—"}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          c="dimmed"
                          style={{
                            maxWidth: "120px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={run.location || ""}
                        >
                          {run.location || "—"}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          </Paper>
        )}
        {qqInfo && qqInfo.baselineCount > 0 && (
          <Text size="sm" c="dimmed" ta="center">
            Includes {qqInfo.baselineCount} Double Q{qqInfo.baselineCount !== 1 ? "s" : ""} from
            baseline counts (no run records)
          </Text>
        )}
        <RunDetailsModal
          run={selectedRun}
          dogNameMap={dogNameMap}
          onClose={() => setSelectedRun(null)}
          onDelete={handleHardDeleteRun}
        />
      </Stack>
    </Container>
  );
};
